import * as fb from '@/library/firebase'
import supportingDetailsSchema from '@/forms/supportingDetailsSchema'
import { mapGetters, mapState, mapActions } from 'vuex'
const sendEmail = fb.funcs.httpsCallable('sendEmail')
export default {
  computed: {
    ...mapState([
      'adminEmail'
    ]),
    ...mapGetters([
      'currentUserProfile'
    ])
  },
  async mounted() {
    // Fill out form values from current user profile
    if (this.currentUserProfile) {
      this.hydrateForms()
    }
  },
  data(){
    return {
      hasFilesToUpload: false,
      pending: false,
      profileFormValues: {},
      supportingDetailsFormValues: {
        figures: []
      },
      showForm: false,
      supportingDetailsSchema: null,
      uploadProgress: 0
    }
  },
  methods: {
    ...mapActions([
      'setTimelineStep'
    ]),
    async asyncForEach(array, callback) {
      if (array && array.length) {
        for (let index = 0; index < array.length; index++) {
          await callback(array[index], index, array)
        }
      }
    },
    hydrateForms() {
      // Hydrate Profile form
      Object.keys(this.currentUserProfile).forEach(key => this.profileFormValues[key] = this.currentUserProfile[key])

      // Hydrate application form
      if (this.applicationFormValues && this.currentUserProfile?.application) {
        Object.keys(this.currentUserProfile.application).forEach(key => {
          const val = this.currentUserProfile.application[key] // e.g. 'filingDate', 'serialNumber', etc
          const fileFields = ['asFiled', 'draft', 'filingReceipt']
          if (fileFields.some(fieldName => key === fieldName)) {
            fileFields.forEach(fieldName => {
              const fileName = this.currentUserProfile.application[fieldName]
              let newVal = fileName;
              if (fileName && typeof fileName === 'string') {
                newVal = [{
                  url: fileName
                }]
              }
              this.applicationFormValues[fieldName] = newVal
            })
          }
          else {
            this.applicationFormValues[key] = val
          }
        })
      }

      // Hydrate Supporting Details form
      if (this.profileFormValues && this.currentUserProfile?.supportingDetails) {
        // Loop through all the supporting details
        Object.keys(this.currentUserProfile.supportingDetails).forEach(key => {
          const val = this.currentUserProfile.supportingDetails[key]; // e.g. 'featuresOfInterest', 'inventionTitle', 'figures', etc.
          // If this is "figures", replace the document string with an array containing an object with a file URL, thus hydrating the file field
          if (key === 'figures' && this.supportingDetailsFormValues.figures) {
            this.currentUserProfile.supportingDetails.figures.forEach(fig => {
              const fileName = fig.document
              if (fileName && typeof fileName === 'string') {
                fig.document = [{
                  url: fileName
                }]
              }
              this.supportingDetailsFormValues.figures.push(fig)
            })
          }
          else {
            this.supportingDetailsFormValues[key] = val
          }
        })

        // Get the dynamically generated form schema from this function
        this.supportingDetailsSchema = supportingDetailsSchema()
      }


      // Hydrate payment form
      if (this.paymentFormValues) {
        this.paymentFormValues.customPrice = this.currentUserProfile?.stripe?.customPrice
      }


      // Set timeout because sometimes the forms wouldn't hydrate without this delay
      setTimeout(() => {
        this.showForm = true
      }, 10);
    },
    async supportingDetailsSubmit() {
      this.uploadAndSave(this.supportingDetailsFormValues, 'supportingDetails', 'figures', ['document'])
    },
    async uploadAndSave(formValues, profileKey, filesArrayKey, fileKeys) {
      this.pending = true
      if (filesArrayKey && fileKeys) {
        // Upload all files
        await this.asyncForEach(formValues[filesArrayKey], async (fig) => {
          fileKeys.forEach(async (fileKey) => {
            // Try to determine if this is an already existing file that was hydrated, or a new one that was selected.
            if (fig[fileKey]?.files?.length) {
              const filename = fig[fileKey].files[0]?.name
              const blob = fig[fileKey].fileList[0]
              fig[fileKey] = filename
              if (blob && !blob.url) {
                this.hasFilesToUpload = true;
                await this.upload(filename, blob)
              }
            }
            else {
              fig[fileKey] = null
            }
          })
        })
        if (profileKey === 'supportingDetails') {
          // Send email to admin
          const to = this.adminEmail
          const subject = 'Action required: review user-submitted supporting documents'
          const url = `${process.env.VUE_APP_SITE_URL}/update-profile?uid=${this.currentUserProfile.uid}#supporting-details`
          let message = `<p>Review ${this.currentUserProfile.firstName}'s supporting documents and contact the user for revisions/further questions or proceed with drafting the application.</p>`
          message += `<p><a href="${url}">${url}</a></p>`
          await sendEmail({ to, subject, message })
          // If the user is on the "you add supporting details" step, go to the next step
          if (this.currentUserProfile.timelineStep === 6) {
            this.setTimelineStep(7)
            this.$router.push({ path: '/dashboard'})
          }
        }
      }
      // For application -- documents aren't nested inside a files array
      else if (!filesArrayKey && fileKeys?.length) {
        // Upload all files
        await this.asyncForEach(fileKeys, async (fileKey) => {
          if (formValues[fileKey]?.files?.length) {
            const filename = formValues[fileKey].files[0]?.name
            const blob = formValues[fileKey].fileList[0]
            if (blob && !blob.url) {
              this.hasFilesToUpload = true
              await this.upload(filename, blob)
            }
          }
        })
      }

      // Reset values from their file input format to simple format
      if (fileKeys && fileKeys.length) {
        fileKeys.forEach(fileKey => {
          if (formValues[fileKey]?.files?.length) {
            formValues[fileKey] = formValues[fileKey].files[0]?.name
          }
          else {
            formValues[fileKey] = null
          }
        })
      }

      // Save form values to DB
      this.$store.dispatch('updateProfile', {
        form: formValues,
        formKey: profileKey,
        uid: this.currentUserProfile.uid
      }).then(() => {
        if (!this.hasFilesToUpload) {
          this.pending = false;
        }
        window.location.reload();
      }).catch(errorMessage => {
        this.pending = false
        this.$notify({
          duration: 10000,
          group: 'default',
          title: 'Error updating',
          type: 'error',
          text: errorMessage
        })
      })
    },
    async upload(filename, blob){
      const uid = this.currentUserProfile.uid;
      const storageRef = fb.storage.ref(`docs/${uid}/${filename}`).put(blob)
      storageRef.on('state_changed',
        snapshot => {
          this.uploadProgress = parseInt((snapshot.bytesTransferred/snapshot.totalBytes) * 100)
        },
        error => {
          console.error(error.message)
          this.pending = false
        },
        () => {
          this.uploadProgress = 100
          this.pending = false
          this.hasFilesToUpload = false
          this.$smoothScroll({
            scrollTo: document.querySelector('.page-top')
          })
          this.showSuccess = true
          setTimeout(() => {
            this.showSuccess = false
          }, 3000)
        }
      )
    },
  },
  watch: {
    pending() {
      if (!this.pending) {
        this.$smoothScroll({
          scrollTo: document.querySelector('.page-top')
        })
        this.showSuccess = true
        setTimeout(() => {
          this.showSuccess = false
        }, 3000)
      }
    },
    async currentUserProfile(profile) {
      // Loop through each key in profile and set form values
      if (profile) {
        this.hydrateForms()
      }
    }
  }
}